import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Translate } from 'react-localize-redux';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import AppState from '@webapp/store/state';
import {
  ChangeMerchType,
  UpdateMerchStore,
} from '@webapp/store/merch/action-creators';
import {
  merchReleaseStartedEvent,
  merchReleaseStepCompletedEvent,
  merchReleaseStepViewedEvent,
} from '@webapp/utils/rudderstack/events';

import {
  ListCatalogResponse,
  CatalogItem,
} from '@services/merch/types/api/list-catalog';
import { SimpleRelease } from '@common/types/release/simple-release';

import MerchProductList from '@webapp/controllers/merch/merch-product-list';
import SelectAssociatedRelease from '@webapp/controllers/merch/select-associated-release';

import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import Typography from '@components/basics/typography';
import MotionOpacity from '@components/motion/motion-opacity';
import CounterFixedButton from '@components/actions/counter-fixed-button';

interface MerchSelectProductPageProps {
  className?: string;
  artistId?: string;
  artistName?: string;
  catalog: ListCatalogResponse;
  releases: SimpleRelease[];
  associatedRelease?: SimpleRelease;
  currentProduct?: CatalogItem;
  isMobile?: boolean;
  isTablet?: boolean;
  nextSection: () => CallHistoryMethodAction;
  updateMerchType: (product: CatalogItem) => ChangeMerchType;
  updateRelease: (
    associatedRelease: SimpleRelease | undefined
  ) => UpdateMerchStore;
}

const mapStateToProps = (state: AppState) => ({
  artistId: state.artist.active?.id,
  artistName: state.artist.active?.name,
  catalog: state.merch.catalog,
  currentProduct: state.merch.active?.product,
  associatedRelease: state.merch.active?.associatedRelease,
  releases: state.release.list.all || [],
  isMobile: state.ui.mobile,
  isTablet: state.ui.tablet,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  nextSection: () => dispatch(push('./color-size')),
  updateMerchType: (product: CatalogItem) =>
    dispatch(new ChangeMerchType(product)),
  updateRelease: (associatedRelease: SimpleRelease | undefined) =>
    dispatch(new UpdateMerchStore({ associatedRelease: associatedRelease })),
});

const MerchSelectProductPage = ({
  className,
  artistId,
  artistName,
  catalog,
  releases,
  currentProduct,
  associatedRelease,
  updateMerchType,
  updateRelease,
  nextSection,
  isMobile,
  isTablet,
}: MerchSelectProductPageProps) => {
  React.useEffect(() => {
    merchReleaseStartedEvent({
      artist_id: artistId,
      artist_name: artistName,
      product_type: 'merch release',
    });
    merchReleaseStepViewedEvent({
      step: 1,
      name: 'select product',
      artist_id: artistId,
      artist_name: artistName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectProduct = (product: CatalogItem) => {
    if (!product) {
      return;
    }

    updateMerchType(product);
    merchReleaseStepCompletedEvent({
      step: 1,
      name: 'select product',
      product_type: product.id,
      artist_id: artistId,
      artist_name: artistName,
    });
  };

  return (
    <MotionOpacity className={className}>
      {!isMobile && (
        <Typography className="mb-30" customFont="semi-bold" type="subheader">
          <Translate id="MERCH.SECTIONS.PRODUCT.TITLE" />
        </Typography>
      )}

      <Grid gap={3}>
        {!!releases.length ? (
          <Box noBorderBottom>
            <Typography className="mb-20" customFont="medium" type="big-title">
              <Translate id="MERCH.SECTIONS.PRODUCT.BOX.TITLE" />
            </Typography>
            <SelectAssociatedRelease
              releases={releases.filter(
                (rel) =>
                  rel.status === 'approved' ||
                  rel.status === 'published' ||
                  rel.status === 'released'
              )}
              selected={associatedRelease}
              onSelect={updateRelease}
            />
          </Box>
        ) : null}
        {catalog && !!catalog.length && (
          <MerchProductList
            products={catalog}
            selected={currentProduct}
            onSelect={selectProduct}
          />
        )}
      </Grid>
      <CounterFixedButton
        id="selectProduct"
        className={!isTablet ? '' : 'mt-30'}
        customDisabledLabel={
          <Translate id="MERCH.SECTION.SELECT_PRODUCT.BUTTON.DISABLED" />
        }
        fixed={!isTablet}
        isValid={currentProduct}
        currentStep={1}
        maxStep={4}
        isLoading={false}
        onNext={nextSection}
      />
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MerchSelectProductPage);
